/**
 * PlayMode Context - Global Default Play Mode State Management
 * Manages the default play mode (GLD for fun mode, CB for real mode) across the application.
 *
 * Default is balance-aware: "CB if you have CB, gold if zero CB." A new player
 * is funded with Gold Coins at signup but holds zero Cluck Bucks, so defaulting
 * everyone to CB left them staring at a 0 balance and bounced them to the store.
 * We therefore start in the always-safe 'gold' mode (every player has GLD) and
 * only promote to 'swp' once we can actually see a Cluck Bucks balance.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { BalanceContext } from "./BalanceContext";

const PlayModeContext = createContext();

/**
 * Play mode values:
 * - 'gold' or 'gld': Fun mode (demo mode, uses GLD currency)
 * - 'swp' or 'cb': Real mode (uses CB/Cluck Bucks currency)
 */
export const PlayModeProvider = ({ children }) => {
  // Safe initial value: 'gold'. Every player receives a Gold Coin grant at
  // signup, so gold is always playable. We never start in CB (which a new
  // player holds zero of) before the real balance is known.
  const [playMode, setPlayModeState] = useState("gold");

  // Read the shared balance WITHOUT throwing when no BalanceProvider is present
  // (isolated component tests mount PlayModeProvider alone). In the real app
  // PlayModeProvider is nested inside BalanceProvider (see App.jsx), so this
  // resolves to the live balance and there is no provider-ordering problem.
  const balanceCtx = useContext(BalanceContext);
  const swpBalance = Number(balanceCtx?.balances?.swp) || 0;
  const balancesLoading = balanceCtx?.loading ?? false;

  // Once the player manually picks a mode, we stop auto-deriving it.
  const userChoseRef = useRef(false);
  // The balance-aware promotion to CB only ever runs once.
  const autoPromotedRef = useRef(false);

  useEffect(() => {
    // Respect a manual choice and never promote twice.
    if (userChoseRef.current || autoPromotedRef.current) return;
    // No balance context (test/standalone): keep the safe 'gold' default.
    if (!balanceCtx) return;
    // Wait until the balance has actually resolved before deciding.
    if (balancesLoading) return;

    if (swpBalance > 0) {
      // Existing Cluck Bucks holder: land them in real (CB) mode.
      setPlayModeState("swp");
      autoPromotedRef.current = true;
    }
    // swpBalance === 0: keep 'gold' but do NOT lock. A zero can be a transient
    // read right after signup, or the player may buy CB later this session; a
    // subsequent funded balance should still be able to promote them once.
  }, [balanceCtx, balancesLoading, swpBalance]);

  // Manual setter. Marks the mode as user-chosen so the auto-default backs off.
  const setPlayMode = useCallback((mode) => {
    userChoseRef.current = true;
    setPlayModeState(mode);
  }, []);

  // Toggle between GLD and CB modes
  const togglePlayMode = useCallback(() => {
    userChoseRef.current = true;
    setPlayModeState((prev) => (prev === "gold" ? "swp" : "gold"));
  }, []);

  // Set to specific mode
  const setToGoldMode = useCallback(() => {
    userChoseRef.current = true;
    setPlayModeState("gold");
  }, []);

  const setToCBMode = useCallback(() => {
    userChoseRef.current = true;
    setPlayModeState("swp");
  }, []);

  // Check if currently in fun mode (GLD)
  const isFunMode = playMode === "gold";

  // Get the currency string for game navigation
  const getCurrency = useCallback(() => {
    return playMode === "gold" ? "GLD" : "CB";
  }, [playMode]);

  const value = {
    playMode,
    setPlayMode,
    togglePlayMode,
    setToGoldMode,
    setToCBMode,
    isFunMode,
    getCurrency,
  };

  return (
    <PlayModeContext.Provider value={value}>
      {children}
    </PlayModeContext.Provider>
  );
};

export const usePlayMode = () => {
  const context = useContext(PlayModeContext);
  if (!context) {
    throw new Error("usePlayMode must be used within a PlayModeProvider");
  }
  return context;
};

export default PlayModeContext;
